<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="medium">
        <template #header>
          <Heading level="h2">{{ t('station_edit.title') }}</Heading>
        </template>

        <div class="tw-space-y-5">
          <Notice v-if="error" type="error" :closable="false" :scroll-to-on-init="false">
            <Text>{{ error }}</Text>
          </Notice>

          <FormField v-model="trackName" :label="t('station_edit.track_name_label')" />
        </div>

        <template #footer="{ reject }">
          <div class="tw-flex tw-w-full tw-justify-end tw-gap-2">
            <Button color="transparent" outlined @click="reject">
              {{ t('common.cancel') }}
            </Button>
            <Button :disabled="!trackNameModified" @click="save">
              {{ t('common.save') }}
            </Button>
          </div>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, FormField, Heading, Modal, ModalContent, Notice, Text } from '@slideslive/fuse-kit/vue';

import useStationsStore from '@/stores/stations';

const { t } = useI18n();

const show = defineModel();

const { station } = defineProps({
  station: { type: Object, required: true },
});

const stationsStore = useStationsStore();
const { refreshOne } = stationsStore;

const originalTrackName = computed(() => station.trackName ?? '');
const trackName = ref('');
const error = ref('');

const trackNameModified = computed(() => trackName.value.trim() !== originalTrackName.value);

watch(show, (open) => {
  if (open) {
    trackName.value = originalTrackName.value;
    error.value = '';
  }
});

async function save() {
  const newName = trackName.value.trim();
  if (!trackNameModified.value || newName === '') return;

  const confirmed = window.confirm(t('station_edit.confirm_modify'));
  if (!confirmed) return;

  error.value = '';

  try {
    const updateResponse = await fetch(`${station.nateApiUrl}/state`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: { track_name: newName } }),
    });

    if (!updateResponse.ok) throw new Error(`Request failed with status ${updateResponse.status}`);

    await refreshOne({ host: station.host });
    show.value = false;
  } catch (e) {
    console.error(e);
    error.value = t('station_edit.error_failed_update');
  }
}
</script>
